<script lang="ts" setup>
    import '../../../css/choice.css';
    import {popAlert,ModalStyles} from '../../lib/Notification'; 
    import {AlignmentType, ButtonDisplaySize,ButtonDisplayStyle,DisplayStyle,IconTypes,ModalSize} from '../../Enums/UiEnums';
    import {ActionButtonStyle} from '../../lib/Types/Types';   
    import {ICol, RenderModeType}  from "../../lib/ChoiceTableHelpers"; 
    import {onMounted,ref,reactive,computed} from 'vue';
    import ChoiceTable from "../../components/ChoiceTable.vue"; 
    import ModalDialog from "../../components/ModalDialog.vue";
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import {NoTzDate ,getNoTzToday} from "../../lib/DateUtils";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import Icon from "../../components/Icon.vue";  
    import FloatInput from '../../components/FloatInput.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import SearchPayer from '../../components/Search/SearchPayer.vue';
    import SearchProvider from '../../components/Search/SearchProvider.vue';
    import SearchServiceLocation from '../../components/Search/SearchServiceLocation.vue';
    import CSVImport from '../../components/File/CSVImport.vue';
    import SimpleList from '../../components/SimpleList.vue'; 
    import {hideWindowObject} from '../../lib/Models/CommonItemsModel'; 
    import {ICsvRecord,CsvColumnCleanup} from '../../lib/Models/CSVFileModel';   
    import {FeeAmountFlags,FeeFlagType,ParameterNames,INegFeeImportSchedule,IFeePractice,INegFeeImport,INegFeeResponse,INegFeeStatus,INegFeeImportFile,ICodeValidation} from '../../lib/Models/FeeImportModel';  
    import {ISearchPayerItem} from '../../lib/Models/SearchModel';
    import {ClaimSimpleLists} from '../../lib/Models/ClaimModel'; 
    import {ErrorReturnMessage,getFeeSetup,setFeeImport} from '../../lib/Api/FeeImport';
    import {IServiceLocationSearchItem } from '../../lib/Api/ServiceLocation';
    import {IProviderSearchItem,IResultAndError} from '../../lib/ChoiceApi'; 
    import {initPinia} from '../../lib/PiniaMgr';
    import {useSimpleListStore} from '../../lib/Stores/SimpleListStore'; 
 
    const minAmount = -999.99;
    const maxAmount = 99999.99;
    const payerSearch = ref<{ clearPayerSearch: () => void } | null>(null);
    const serviceLocationSearch = ref<{ clearServiceLocationSearch: () => void } | null>(null);
    const providerSearch = ref<{ clearProviderSearch: () => void } | null>(null);
    const simpleListStore = useSimpleListStore(initPinia());
    const FeeAmountDescription = ["Start","Fee","Allowed","In-House","Tax","RVU"]; 
    const importFileHeaders = ["ProcedureCode", "FeeType", "FeePerUnit", "AllowedAmount", "InHouseCost", "TaxAmount", "RvuAmount", "BaseUnits"];
    const templateExmample = ["Example Code", "F", "10", "11", "12", "13", "14", "1.0"];
    const columnCleanup = [CsvColumnCleanup.String, CsvColumnCleanup.String, CsvColumnCleanup.AmountCharacters, CsvColumnCleanup.AmountCharacters,CsvColumnCleanup.AmountCharacters, CsvColumnCleanup.AmountCharacters, CsvColumnCleanup.AmountCharacters, CsvColumnCleanup.String];

    const displayModalWindow = computed<boolean>(() => 
        formData.ModelDisplay.parameterHelp || 
        formData.ModelDisplay.groupingHelp || 
        formData.ModelDisplay.codeValidationHelp || 
        formData.ModelDisplay.fileHelp ||
        formData.ModelDisplay.dataFileSuccess ||
        formData.ModelDisplay.dataFileUpdated ||
        formData.ModelDisplay.dataFileError
    );
    const displayModalSize = computed<ModalSize>(() => {
        if (formData.ModelDisplay.parameterHelp || formData.ModelDisplay.fileHelp || formData.ModelDisplay.dataFileUpdated || formData.ModelDisplay.dataFileError) {
            return ModalSize.Large;
        }
        return formData.ModelDisplay.groupingHelp || formData.ModelDisplay.codeValidationHelp || formData.ModelDisplay.dataFileSuccess ? ModalSize.Medium : ModalSize.Small;
    });  

    const importDisabled = computed(() => 
        !props.writeEnabled || 
        !formData.Validation.File || 
        !formData.Validation.StartDateValid || 
        !formData.Validation.ProviderValid ||
        !formData.Validation.AmountValid 
    );
    const amountDisabledFlags = computed(() => {
        return formData.ParameterFlags.map((flag, index) => 
            index === 0 ? false : [FeeAmountFlags.Clear, FeeAmountFlags.Replace, FeeAmountFlags.Maintain].includes(flag)
        );
    });
    const financialClassListLoaded = computed(() => 
        !!simpleListStore?.listResult[ClaimSimpleLists.FinancialClass]
    );  
    const feeAmountFlagOptions = computed(() => {
        return Object.entries(FeeAmountFlags).map(([key, value]) => ({label: key, value: value}));
    });
      
   const reportTable = ref<InstanceType<typeof ChoiceTable>>();
   const reportData = ref<(INegFeeImportFile)[]>([]);
   const reportColumns = ref<ICol[]>([ 
        { Name: 'Key', DataField: 'procedureCode' }, 
        { Name: 'Type', DataField: 'feeType' },  
        { Name: 'Fee', DataField: 'feePerUnit',Alignment: AlignmentType.Right}, 
        { Name: 'Allowed', DataField: 'allowedAmount',Alignment: AlignmentType.Right}, 
        { Name: 'In-House', DataField: 'inHouseCost',Alignment: AlignmentType.Right }, 
        { Name: 'Tax', DataField: 'taxAmount',Alignment: AlignmentType.Right}, 
        { Name: 'RVU', DataField: 'rvuAmount',Alignment: AlignmentType.Right}, 
        { Name: 'Base Units', DataField: 'baseUnits' },
        { Name: 'Message', DataField: 'feeMessage',RenderMode: RenderModeType.HTML }
    ]); 
    const formData = reactive <
    {       
        ParameterAmounts: (number | undefined)[],
        ParameterFlags: FeeAmountFlags[],
        GroupingIds: number[], 
        Practice: {EnablePractice: boolean, UsePractice: boolean, EntityId: number},
        StartDay: NoTzDate,
        FinClassID?: number|string
        ScheduleParameters?: INegFeeImportSchedule,
        Validation: {Message: string, File: boolean, ProviderValid: boolean, StartDateValid: boolean, AmountValid: boolean},
        ModelDisplay : {parameterHelp: boolean, groupingHelp: boolean, codeValidationHelp: boolean, fileHelp: boolean, dataFileSuccess: boolean, dataFileUpdated: boolean, dataFileError: boolean},
        CodeValidation: ICodeValidation,
        FileImportID : number,
        FileInformation?: ICsvRecord,
        ProviderSelection?: IProviderSearchItem,
        FeeStatus?: INegFeeStatus,
        CodeToggle: IconTypes
    }>({   
        ParameterAmounts: [undefined,undefined,undefined,undefined,undefined,undefined],
        ParameterFlags: [FeeAmountFlags.Clear,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain],
        GroupingIds: [0,0,0,0,0,0],
        Practice: {EnablePractice: true, UsePractice: false, EntityId: 0},
        StartDay: getNoTzToday(),
        Validation: {Message: "", File: false, ProviderValid: true, StartDateValid: true, AmountValid: true},
        ModelDisplay : {parameterHelp: false, groupingHelp: false, codeValidationHelp: false, fileHelp: false, dataFileSuccess: false, dataFileUpdated: false, dataFileError: false},
        CodeValidation: {RequiredModifiers: true, RestrictedModifiers: true, RequiredProcedures: true, RestrictedProcedures: true, RequiredDiagnoses: true, RestrictedDiagnoses: true, RequiredPrimaryDiagnoses: true, RestrictedPrimaryDiagnoses: true},
        FileImportID: 1,
        CodeToggle: IconTypes.ToggleOn
    });
    const props = withDefaults(defineProps<{  
        writeEnabled: boolean
    }>(),
    {
        writeEnabled: false,
    });
    onMounted(async () => { 
        await simpleListStore.getSimpleWhereList(ClaimSimpleLists.FinancialClass,""); 
        hideWindowObject("LoadingMessage"); 
        await getSetupInformation();
    });
    
    async function getSetupInformation() : Promise<void> {        
        const fee_setup: IResultAndError<IFeePractice> = await getFeeSetup();
        if (!fee_setup.success) {
            await popAlert((fee_setup.error?.error ?? ErrorReturnMessage.feeSetup),"Setup Details",ModalStyles.danger);
            return;
        }
        formData.Practice = {EnablePractice: fee_setup.success.usePractice, UsePractice: false, EntityId: fee_setup.success.practiceID};
    }
    async function clearParameters() : Promise<void> {  
        formData.FileImportID++;
        formData.StartDay = getNoTzToday();
        formData.Practice.UsePractice = false; 
        formData.GroupingIds[ParameterNames.Practice] = 0;
        formData.FinClassID = "";
        formData.GroupingIds[ParameterNames.FinancialClass] = 0;
        formData.ParameterAmounts = [undefined,undefined,undefined,undefined,undefined,undefined];
        formData.ParameterFlags = [FeeAmountFlags.Clear,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain,FeeAmountFlags.Maintain];
        formData.CodeValidation = {RequiredModifiers: true, RestrictedModifiers: true, RequiredProcedures: true, RestrictedProcedures: true, RequiredDiagnoses: true, RestrictedDiagnoses: true, RequiredPrimaryDiagnoses: true, RestrictedPrimaryDiagnoses: true};
        clearPayerSearch();
        clearServiceLocationSearch();
        clearProviderSearch(); 
        clearFileInformation();
        validateInputs();
    }
    function setScheduleParameters() : void {
        formData.ScheduleParameters = 
        {   
            StartDate: formData.StartDay, 
            PracticeId: formData.GroupingIds[ParameterNames.Practice],
            FinancialClassId: formData.GroupingIds[ParameterNames.FinancialClass],
            PayerId: formData.GroupingIds[ParameterNames.Payer],
            BillingProviderId: formData.GroupingIds[ParameterNames.BillingProvider],
            ServiceLocationId: formData.GroupingIds[ParameterNames.ServiceLocation],
            FeeFlag: formData.ParameterFlags[FeeFlagType.feeFlag],
            FeePerUnit: formData.ParameterAmounts[FeeFlagType.feeFlag],
            AllowedFlag: formData.ParameterFlags[FeeFlagType.allowedFlag],
            AllowedAmount: formData.ParameterAmounts[FeeFlagType.allowedFlag],
            InHouseFlag: formData.ParameterFlags[FeeFlagType.inHouseFlag],
            InHouseCost: formData.ParameterAmounts[FeeFlagType.inHouseFlag],
            TaxFlag: formData.ParameterFlags[FeeFlagType.taxFlag],
            TaxAmount: formData.ParameterAmounts[FeeFlagType.taxFlag],
            RvuFlag: formData.ParameterFlags[FeeFlagType.rvuFlag],
            RvuAmount: formData.ParameterAmounts[FeeFlagType.rvuFlag]
        }
    }
    async function closeWindow() : Promise<void> {  
        window.close();
    }  
    async function performImport() : Promise<void> {
        setScheduleParameters();
        if (!formData.ScheduleParameters) {
            return;
        }
        const fee_import_payload : INegFeeImport = {FileName: formData.FileInformation?.Name ?? "UnknownFileName.csv", ValidationData: [formData.CodeValidation] ,ScheduleData: [formData.ScheduleParameters], DataFile: formData.FileInformation?.Data}; 
        const fee_response: IResultAndError<INegFeeResponse> = await setFeeImport(fee_import_payload);
        if (!fee_response.success) {
            await popAlert((fee_response.error?.error ?? ErrorReturnMessage.feeLoad),"Import Details",ModalStyles.danger);
            return;
        }

        const reort_data = fee_response.success.dataFile
            .filter(item => (item.feeMessage ?? "") != "")
            .map((item) => {
                return {
                    procedureCode: item.procedureCode,
                    feeType: (item.feeType == "*" ? "" : item.feeType),
                    feePerUnit: item.feePerUnit,
                    allowedAmount: item.allowedAmount,
                    inHouseCost: item.inHouseCost,
                    taxAmount: item.taxAmount,
                    rvuAmount: item.rvuAmount,
                    baseUnits: item.baseUnits,
                    feeMessage: (item.feeMessage ?? "").replace(/~/g, '<br/>')
                };
            });

        formData.FeeStatus = fee_response.success.feeStatus;
        formData.ModelDisplay.dataFileError = fee_response.success.feeStatus.errorCode;
        formData.ModelDisplay.dataFileSuccess = !fee_response.success.feeStatus.errorCode;
        if ((formData.ModelDisplay.dataFileSuccess) && (reort_data.length)) {
            formData.ModelDisplay.dataFileUpdated = true;
            formData.ModelDisplay.dataFileSuccess = false;
        } 

        if ((formData.ModelDisplay.dataFileError) || (formData.ModelDisplay.dataFileUpdated)) {
            reportData.value = reort_data
        }
    }
    function validateInputs() : void {
        // Start Date Validation
        if (!formData.StartDay) {
            Object.assign(formData.Validation, {
                Message: "Start date is required.",
                StartDateValid: false
            });
            return;
        }
        const today = new Date(getNoTzToday());
        const startDate = new Date(formData.StartDay);
        if (startDate < today) {
            Object.assign(formData.Validation, {
                Message: "Start date must be today or in the future.",
                StartDateValid: false
            });
            return;
        }
        formData.Validation.StartDateValid = true; 

        // Amount Validation 
        const amountTypes = [
            { type: FeeFlagType.feeFlag, label: "Fee" },
            { type: FeeFlagType.allowedFlag, label: "Allowed" },
            { type: FeeFlagType.inHouseFlag, label: "In-House" },
            { type: FeeFlagType.taxFlag, label: "Tax" },
            { type: FeeFlagType.rvuFlag, label: "RVU" }
        ];

        for (const { type, label } of amountTypes) {
            const amount = formData.ParameterAmounts[type] ?? 0;
            if (amount < minAmount || amount > maxAmount) {
            Object.assign(formData.Validation, {
                Message: `${label} amount must be within ${minAmount} and ${maxAmount.toLocaleString()}.`,
                AmountValid: false
            });
            return;
            }
        }
        formData.Validation.AmountValid = true; 

        // Provider Validation
        if ((formData.ProviderSelection?.rendering_provider ?? "yes").toLocaleLowerCase() !== "yes") {
            Object.assign(formData.Validation, {
                Message: "Provider is not a billing provider.",
                ProviderValid: false
            });
            formData.GroupingIds[ParameterNames.BillingProvider] = 0;
            return;
        }
        formData.Validation.ProviderValid = true; 

        // File Validation
        if (!formData.FileInformation) {
            formData.Validation.Message = "Missing import file.";
            formData.Validation.File = false;
            return; 
        }
        if (!formData.FileInformation.Success) {
            formData.Validation.Message = formData.FileInformation.ValidationMessage ?? "File is not valid.";
            formData.Validation.File = false;
            return; 
        }
        formData.Validation.File = true; 
        // Reset Message
        formData.Validation.Message = ""; 
    }
    function updatePracticeInformation() : void { 
        formData.GroupingIds[ParameterNames.Practice] = formData.Practice.UsePractice ? formData.Practice.EntityId : 0;
    }
    function onChangePayer(payer: ISearchPayerItem) : void {
        formData.GroupingIds[ParameterNames.Payer] = payer?.payer_id ? +payer.payer_id : 0; 
    }
    function onChangeProvider(provider?: IProviderSearchItem) : void {
        formData.ProviderSelection = provider;
        validateInputs();
        formData.GroupingIds[ParameterNames.BillingProvider] = provider?.billing_provider_id ? +provider.billing_provider_id : 0;
    }
    function onChangeServiceLocation(serviceLocation: IServiceLocationSearchItem) : void {
        formData.GroupingIds[ParameterNames.ServiceLocation] = serviceLocation?.service_location_id ? +serviceLocation.service_location_id : 0; 
    }
    function onChangeFinancialClass(selectedItem : any) : void { 
        formData.GroupingIds[ParameterNames.FinancialClass] = +selectedItem?.value || 0; 
    }  
    function validateFile(fileInformation : ICsvRecord) : void {
        formData.FileInformation = fileInformation;
        formData.Validation.File = formData.FileInformation.Success;
        validateInputs();
    }
    function clearPayerSearch() : void { 
        if (payerSearch.value) { 
            payerSearch.value.clearPayerSearch();
        }
    }
    function clearServiceLocationSearch() : void { 
        if (serviceLocationSearch.value) { 
            serviceLocationSearch.value.clearServiceLocationSearch();
        }
    }
    function clearProviderSearch() : void { 
        if (providerSearch.value) { 
            providerSearch.value.clearProviderSearch();
        }
    } 
    function  clearFileInformation() : void { 
        formData.FileInformation = {Name: "", Success: false, ValidationMessage: "Missing import file."};
    }
    function resetAmounts(index: number): void { 
        formData.ParameterAmounts[index] = 
            formData.ParameterFlags[index] === FeeAmountFlags.Clear || formData.ParameterFlags[index] === FeeAmountFlags.Maintain || formData.ParameterFlags[index] === FeeAmountFlags.Replace 
            ? undefined 
            : formData.ParameterAmounts[index];
    }
    async function openParameterHelp() : Promise<void> {
        formData.ModelDisplay.parameterHelp = true;
    }  
    async function openGroupingHelp() : Promise<void> {   
        formData.ModelDisplay.groupingHelp = true;
    }  
    
    async function openValidationHelp() : Promise<void> { 
        formData.ModelDisplay.codeValidationHelp = true;
    } 
    async function openFileHelp() : Promise<void> { 
        formData.ModelDisplay.fileHelp = true;
    } 
    async function ModalHiddenResponse() : Promise<void> {
        formData.ModelDisplay.parameterHelp = false;
        formData.ModelDisplay.groupingHelp = false;
        formData.ModelDisplay.codeValidationHelp = false;
        formData.ModelDisplay.fileHelp = false; 
        formData.ModelDisplay.dataFileError = false; 
        formData.ModelDisplay.dataFileSuccess = false; 
        formData.ModelDisplay.dataFileUpdated = false; 
    } 
    function toggleCodeValidationChecks() : void { 
        let new_value = true; 

        if (formData.CodeToggle == IconTypes.ToggleOn) {
            formData.CodeToggle = IconTypes.ToggleOff;
            new_value = false;
        } else {
            formData.CodeToggle = IconTypes.ToggleOn; 
        }
        formData.CodeValidation = {RequiredModifiers: new_value, RestrictedModifiers: new_value, RequiredProcedures: new_value, RestrictedProcedures: new_value, RequiredDiagnoses: new_value, RestrictedDiagnoses: new_value, RequiredPrimaryDiagnoses: new_value, RestrictedPrimaryDiagnoses: new_value};
        
    }
</script> 

<style scoped>
    @import url('../../../css/FeeScheduleGrid.css');
</style>

<template> 
    <WindowHeaderFooter
    :headerHeight="3.5"
    :footerHeight="3.5">
        <template v-slot:WindowHeader>
            <div class="isalus-page-title-large pt-2 pb-2">Fee Schedule Import</div>
        </template>
        <template v-slot:WindowBody>
            <div class="row label-isalus-small"> 
                <div class="col-11 pb-1 label-isalus-medium text-primary">
                    <span class="pe-1">Parameters:</span>
                    <Icon 
                        :icon="IconTypes.CircleQuestion" 
                        :displayStyle="DisplayStyle.Primary" 
                        :class="'label-isalus-small text-isalus-primary'"
                        :title="`How are these parameters used?`"
                        @click="openParameterHelp"
                    /> 
                </div>
            </div>  
            <div class="row label-isalus-small ps-2"> 
                <div class="grid-container-fee-parameters col-11 pb-1" v-for="index in Array.from({ length: Math.ceil(FeeAmountDescription.length / 2) }, (_, i) => i * 2)" :key="index">
                    <div v-if="index == 0" class="label-fee-parameters">Start:</div>
                    <div v-if="index == 0" class="label-isalus-small">
                        <DatePickerNoTZ
                            v-model:value="formData.StartDay" 
                            :clearable="false" 
                            :required=true
                            :errorDisplay=false 
                            :placeholder="'Search Start Date'"
                            :cssFramework="'vanilla'"
                            @update:value="()=>validateInputs()"
                        />
                    </div> 
                    <div v-if="index == 0"></div>   
                    <div v-if="index > 0" class="label-fee-parameters">{{FeeAmountDescription[index]}}:</div>
                    <div v-if="index > 0" > 
                        <select 
                            v-model="formData.ParameterFlags[index]" 
                            class="label-isalus-small" 
                            style="width: 100%"
                            @change="resetAmounts(index)">
                            <option v-for="option in feeAmountFlagOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div> 
                    <div v-if="index > 0" >
                        <FloatInput 
                            v-model:numberValue="formData.ParameterAmounts[index]"
                            :maxLength="10"
                            :startingRange="minAmount"
                            :endingRange="maxAmount"
                            :minPrecision="(index == 4) ? 6 : 2"
                            :maxPrecision="(index == 4) ? 6 : 2"
                            :positive="false"
                            :errorDisplay="false"
                            :cssFramework="'vanilla'"
                            :disabled="amountDisabledFlags[index]"
                            :placeholder="`${FeeAmountDescription[index]} Amount`"
                            @update:numberValue="validateInputs"
                        />
                    </div>  
                    <div class="label-fee-parameters">{{FeeAmountDescription[index + 1]}}:</div>
                    <div> 
                        <select 
                            v-model="formData.ParameterFlags[index + 1]" 
                            class="label-isalus-small" 
                            style="width: 100%"
                            @change="resetAmounts(index + 1)">
                            <option v-for="option in feeAmountFlagOptions" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div> 
                    <div>
                        <FloatInput 
                            v-model:numberValue="formData.ParameterAmounts[index + 1]"
                            :maxLength="10"
                            :startingRange="minAmount"
                            :endingRange="maxAmount"
                            :minPrecision="(index + 1 == 4) ? 6 : 2"
                            :maxPrecision="(index + 1 == 4) ? 6 : 2"
                            :positive="false"
                            :errorDisplay="false"
                            :cssFramework="'vanilla'"
                            :disabled="amountDisabledFlags[index + 1]"
                            :placeholder="`${FeeAmountDescription[index + 1]} Amount`"
                            @update:numberValue="validateInputs"
                        />
                    </div>                  
                </div> 
            </div> 
            <div class="row label-isalus-small"> 
                <div class="col-11 pb-1 pt-3 label-isalus-medium text-primary">
                    <span class="pe-1">Grouping:</span>
                    <Icon 
                        :icon="IconTypes.CircleQuestion" 
                        :displayStyle="DisplayStyle.Primary" 
                        :class="'label-isalus-small text-isalus-primary'"
                        :title="`How are these groupings used?`"
                        @click="openGroupingHelp"
                    /> 
                </div>
            </div>  
            <div v-if="formData.Practice.EnablePractice" class="row label-isalus-small ps-2">               
                <div class="col-11 pb-2">
                    <Checkbox 
                        v-model="formData.Practice.UsePractice" 
                        :title="'Use Practice as part of the grouping'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Use Practice ({{formData.Practice.EntityId}}) as part of the grouping</span> 
                </div>
            </div>  
            <div class="row label-isalus-small ps-2"> 
                <div class="col-5 pb-1">
                    <div class="label-isalus-small">
                        <div>FinancialClass</div>
                        <SimpleList v-if="financialClassListLoaded"
                            v-model:selected="formData.FinClassID"
                            :listName="ClaimSimpleLists.FinancialClass"
                            :includeHeader="true"
                            :includeBlank="true" 
                            @onChange="onChangeFinancialClass" 
                        />
                    </div>
                </div>
                <div class="col-6 pb-1">
                    <SearchPayer
                        ref="payerSearch"
                        label="Payer"
                        :addToBaseClass="'label-isalus-small'" 
                        :showClearSearch="true"
                        :enableAutoSearch="true" 
                        @onChange="onChangePayer"
                    />
                </div>
            </div>
            <div class="row ps-2">
                <div class="col-5 pb-1">
                    <SearchProvider  
                        ref="providerSearch"
                        label="Provider" 
                        :addToBaseClass="'label-isalus-small'" 
                        :enable-auto-search="true" 
                        :show-clear-search="true" 
                        @onChange="onChangeProvider"
                    />
                </div>                
                <div class="col-6 pb-1">
                    <SearchServiceLocation  
                        ref="serviceLocationSearch"
                        label="Location" 
                        :addToBaseClass="'label-isalus-small'" 
                        :enable-auto-search="true" 
                        :show-clear-search="true" 
                        :limitToClaimLocations="true"
                        @onChange="onChangeServiceLocation"
                    />
                </div>
            </div>
            <div class="row label-isalus-small"> 
                <div class="col-11 pb-1 pt-3 label-isalus-medium text-primary">
                    <span class="pe-1">Code Validation:</span>
                    <Icon 
                        :icon="IconTypes.CircleQuestion" 
                        :displayStyle="DisplayStyle.Primary" 
                        :class="'label-isalus-small text-isalus-primary'"
                        :title="`How is code validation used?`"
                        @click="openValidationHelp"
                    /> 
                </div>
            </div>  
            <div class="row label-isalus-small pb-2 ps-2">
                <div class="col-11">
                    Select the code validations you would like to be copied from prior fee schedules.
                </div>
            </div> 
            <div class="row label-isalus-small ps-2">
                <div class="col-2">
                    <Checkbox 
                        v-model="formData.CodeValidation.RequiredModifiers" 
                        :title="'Copy Required Modifiers'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Required Modifiers</span> 
                </div>
                <div class="col-3">
                    <Checkbox 
                        v-model="formData.CodeValidation.RequiredProcedures" 
                        :title="'Copy Required Procedures'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Required Procedures</span> 
                </div>
                <div class="col-3">
                    <Checkbox 
                        v-model="formData.CodeValidation.RequiredDiagnoses" 
                        :title="'Copy Required Diagnoses'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Required Diagnoses</span> 
                </div>
                <div class="col-3">
                    <Checkbox 
                        v-model="formData.CodeValidation.RequiredPrimaryDiagnoses" 
                        :title="'Copy Required Primary Diagnoses'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Required Primary Diagnoses</span> 
                </div>
            </div>
            <div class="row label-isalus-small ps-2">
                <div class="col-2">
                    <Checkbox 
                        v-model="formData.CodeValidation.RestrictedModifiers" 
                        :title="'Copy Restricted Modifiers'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Restricted Modifiers</span> 
                </div>
                <div class="col-3">
                    <Checkbox 
                        v-model="formData.CodeValidation.RestrictedProcedures" 
                        :title="'Copy Restricted Procedures'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Restricted Procedures</span> 
                </div>
                <div class="col-3">
                    <Checkbox 
                        v-model="formData.CodeValidation.RestrictedDiagnoses" 
                        :title="'Copy Restricted Diagnoses'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Restricted Diagnoses</span> 
                </div>
                <div class="col-3">
                    <Checkbox 
                        v-model="formData.CodeValidation.RestrictedPrimaryDiagnoses" 
                        :title="'Copy Restricted Primary Diagnoses'"
                        @update:modelValue="()=>updatePracticeInformation()" />  
                    <span label class="label-isalus-small">Restricted Primary Diagnoses</span> 
                </div>
            </div> 
            <div class="row ps-2 pt-2">
                <div class="col-9 isalus-text-right"> 
                    <Icon 
                        :icon="formData.CodeToggle" 
                        :displayStyle="DisplayStyle.Primary" 
                        :class="'label-isalus-large text-isalus-primary'"
                        :title="`Toggle code validations`"
                        @click="toggleCodeValidationChecks"
                    />  
                </div>
                <div class="col-2 label-isalus-small"> 
                    Toggle code selections
                </div> 
            </div> 
            <div class="row"> 
                <div class="col-12 pb-1 pt-3 label-isalus-medium text-primary">
                    <span class="pe-1">Import File:</span>
                    <Icon 
                        :icon="IconTypes.CircleQuestion" 
                        :displayStyle="DisplayStyle.Primary" 
                        :class="'label-isalus-small text-isalus-primary'"
                        :title="`How is this file used?`"
                        @click="openFileHelp"
                    /> 
                </div>
            </div>
            <div class="row label-isalus-small ps-2">
                <div class="col-11 pb-1">
                    <CSVImport
                        :fileImportID="formData.FileImportID"
                        :maxFileSizeMB="5"
                        :fileColumnHeaders="importFileHeaders"
                        :fileExampleData="templateExmample"
                        :fileColumnCleanUp="columnCleanup"
                        @fileChanged="validateFile"
                     />
                </div>
            </div> 
        </template>
        <template v-slot:WindowFooter>
            <div class="row">
                <div class="col-9 label-isalus-small text-danger">
                    <span v-if="formData.Validation.Message">{{ formData.Validation.Message }}</span>
                </div>
                <div class="col-3 text-end ">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Erase"
                        :text="'Clear'"
                        :spinText="'Clear'"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="clearParameters"
                        :endingPadding="1"
                    /> 
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Import"
                        :text="'Import'"
                        :spinText="'Import'"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="performImport"
                        :disabled="importDisabled"
                        :endingPadding="1"
                    /> 
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Close"
                        :text="'Close'"
                        :spinText="'Close'"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="closeWindow"
                    /> 
                </div>
            </div>
        </template>
    </WindowHeaderFooter> 
    <ModalDialog 
        :show="displayModalWindow"
        @hidden="ModalHiddenResponse"  
        :size="displayModalSize"
        :closeOnClickOutside=false>
        <template #title>
            <span v-if="formData.ModelDisplay.parameterHelp">Parameter Help</span>
            <span v-if="formData.ModelDisplay.groupingHelp">Groupings Help</span>
            <span v-if="formData.ModelDisplay.codeValidationHelp">Code Validation Help</span>
            <span v-if="formData.ModelDisplay.fileHelp">File Help</span>
            <span v-if="formData.ModelDisplay.dataFileSuccess">Fee Schedule Import was successful</span>
            <span v-if="formData.ModelDisplay.dataFileUpdated">Fee Schedule Import was successful</span>
            <span v-if="formData.ModelDisplay.dataFileError">Fee Schedule Import was Unsuccessful</span>
        </template>
        <div v-if="formData.ModelDisplay.parameterHelp" class="label-isalus-small">  
            <div>
                Parameters are used to set the start date and define how the amount values are updated from the import file. 
                Amount values from the import file will <u>ALWAYS</u> override the parameter amounts.<br/>
            </div>
            <div style="height: 19rem; overflow-y: scroll;">
                <br/>&#8226; <b>Start Date:</b> <i>Required, the date fee schedule will come into effect.</i>
                <br/><br/>&#8226; <b>Fee Flag:</b> <i>Required, determines how the fee amount is calculated in the new fee schedule.</i>
                <br/>&#8226; <b>Fee Amount:</b> <i>Optional, the amount to use for the fee schedule (ranging from {{minAmount}} to {{maxAmount}}).</i>
                <br/><br/>&#8226; <b>Allowed Flag:</b> <i>Required, determines how the allowed amount is calculated in the new fee schedule.</i>
                <br/>&#8226; <b>Allowed Amount:</b> <i>Optional, the amount to use for the allowed schedule (ranging from {{minAmount}} to {{maxAmount}}).</i>
                <br/><br/>&#8226; <b>In-House Flag:</b> <i>Required, determines how the in-house cost is calculated in the new fee schedule.</i>
                <br/>&#8226; <b>In-House Cost:</b> <i>Optional, the amount to use for the in-house cost schedule (ranging from {{minAmount}} to {{maxAmount}}).</i>
                <br/><br/>&#8226; <b>Tax Flag:</b> <i>Required, determines how the tax amount is calculated in the new fee schedule.</i>
                <br/>&#8226; <b>Tax Amount:</b> <i>Optional, the amount to use for the tax schedule (ranging from {{minAmount}} to {{maxAmount}}, however the newly calculated amount cannot exceed a value of 1).</i>
                <br/><br/>&#8226; <b>RVU Flag:</b> <i>Required, determines how the RVU amount is calculated in the new fee schedule.</i>
                <br/>&#8226; <b>RVU Amount:</b> <i>Optional, the amount to use for the RVU schedule (ranging from {{minAmount}} to {{maxAmount}}).</i>
            </div>
            <div>
                <br/><br/><i>Note: The flag parameters work as follows:</i>
                <br/>&#8226; The "Maintain" flag will use the value from the last fee schedule. If selected the parameter amount is disabled.
                <br/>&#8226; The "Clear" flag will set the amount value to 0. If selected the parameter amount is disabled.
                <br/>&#8226; The "Replace" flag will set the amount value to either the value in the fee schedule or 0. If selected the parameter amount is disabled.
                <br/>&#8226; The "AddOrSubtract" flag will calculate the amount value to be the value in the last fee schedule plus the value in the fee schedule or the parameter amount.
                <br/>&#8226; The "Percentage" flag will calculate the amount value to be the value in the last fee schedule multiplied by the value (divided by 100 - as this is a percent) in the fee schedule or the parameter amount.
            </div>             
        </div>
        <div v-if="formData.ModelDisplay.groupingHelp" class="label-isalus-small">  
            The grouping is used to determine which fee schedule to use when importing the file. The following groupings are available - in any combination:<br/>
            <br/><b>1. Practice:</b> <i>Not available for all clients.</i>
            <br/><b>2. Financial Class</b>
            <br/><b>3. Payer</b>
            <br/><b>4. Billing Provider</b>
            <br/><b>5. Service Location</b>
            <br/><br/><i>If all values are blank, then the <u>base</u> fee schedule will be updated.</i> 
        </div>
        <div v-if="formData.ModelDisplay.codeValidationHelp" class="label-isalus-small">  
            The Code Validation options are used to determine which code validation data is copied from the last fee schedule. The following options are available - in any combination:<br/>
            <br/><b>1. Required and/or Restricted Modifiers.</b>
            <br/><b>2. Required and/or Restricted Procedures.</b>
            <br/><b>3. Required and/or Restricted Diagnoses.</b>
            <br/><b>4. Required and/or Restricted Primary Diagnoses.</b>
            <br/><br/><i><b>Note:</b>&nbsp;These validations will be copied to the new fee schedule, it will not affect those codes that get "updated".</i>  
        </div>
        <div v-if="formData.ModelDisplay.fileHelp" class="label-isalus-small"> 
            The import file should be a *.CSV file with the following columns - in order:<br/>
            <br/><b>1. ProcedureCode:</b><i> Required, No more than 10 characters in length.</i>
            <br/><b>2. FeeType:</b><i> Optional, 1 character ("F", "P" or blank). If blank, the value from the last fee schedule or "F" will be used.</i>
            <br/><br/><i>Amount fields: Each of the following 5 fields are optional, numbers/currency with up to 2 decimal places. These values override the incoming parameter amount value." </i>
            <br/><b>3. FeePerUnit.</b>
            <br/><b>4. AllowedAmount.</b>
            <br/><b>5. InHouseCost.</b>
            <br/><b>6. TaxAmount.</b><i> Up to six decimal places allowed.</i>
            <br/><b>7. RvuAmount.</b>
            <br/><br/><b>8. BaseUnits:</b><i> Optional, No more than 10 characters in length. If blank, the value from the last fee schedule will be used.</i>  
            <br>
            <br/><i><b>General Notes:</b></i>
            <br/><i>The incoming file will be have the data scrubbed, removing dollar signs, commas and quotes, etc. as needed.</i>
            <br/><i>You can download a sample file by clicking the Template button.</i>
            <br/><i>The file must be a text CSV file.</i>
            <br/><i>The file must be less than 5MB in size with a file name not exceeding 100 characters in length.</i>
            <br/><i>The first row <u>must</u> contain the column headers exactly as displayed below.</i>
            <br/><i>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ProcedureCode, FeeType, FeePerUnit, AllowedAmount, InHouseCost, TaxAmount, RvuAmount, BaseUnits</i>
        </div>
        <div v-if="formData.ModelDisplay.dataFileSuccess" class="label-isalus-small"> 
            <div class="row"> 
                <div class="col-12">The Fee schedule was successfully applied.</div> 
                <div class="col-12 text-success">{{formData.FeeStatus?.feeMessage}}</div> 
            </div>
        </div>
        <div v-if="formData.ModelDisplay.dataFileUpdated || formData.ModelDisplay.dataFileError" class="label-isalus-small"> 
            <div v-if="formData.ModelDisplay.dataFileUpdated" class="row"> 
                <div class="col-12">The Fee schedule was successfully applied, with the following updates.</div> 
                <div class="col-12 text-success">{{formData.FeeStatus?.feeMessage}}</div> 
            </div>
            <div v-if="formData.ModelDisplay.dataFileError" class="row"> 
                <div class="col-12">Unable to import file. Please check the parameters and file data and try again.</div> 
                <div class="col-12 text-danger">{{formData.FeeStatus?.feeMessage ?? "Unknown error occurred."}}</div> 
            </div>
            <div class="row pt-2">
                <div class="col-12" style="font-size: .75rem;">
                    <ChoiceTable 
                        ref="reportTable"
                        :columns="reportColumns" 
                        :data="reportData" 
                        :show-filters="false" 
                        :show-footer="false" 
                        :allowSorting="true"
                        :key-value-data-member="'procedureCode'">
                    </ChoiceTable>
                </div>
            </div>
        </div>
        <template #footer>  
            <ActionButtonV2 
                :action="ActionButtonStyle.Close"
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                :spinText="'Closing'"
                :performAction="ModalHiddenResponse"
            /> 
        </template>        
    </ModalDialog>
</template>
